// Hook custom per recuperare i comuni filtrati per sigla della provincia.
// Nota: uso axios e TypeScript per tipizzare la risposta e gestire errori e loading.

import { useState, useEffect } from "react";
import axios from "axios";

// Prendo la base URL dell'API dalle variabili d'ambiente
const API_BASE_URL = import.meta.env.VITE_APP_API_BASE_URL;

// Tipizzazione di un singolo comune
interface Comune {
    codiceIstatComuneAlfa: string;
    nomeIt: string;
    cap: string;
    siglaProvincia: string;
}

/**
 * useGetComuni
 * Recupera i comuni dato il codice della provincia
 *
 * @remarks
 * - `comuni` è un array di oggetti Comune
 * - `loading` è true mentre la chiamata è in corso
 * - `error` contiene un messaggio di errore in caso di fallimento
 */
export const useGetComuni = (siglaProvincia: string) => {
    const [comuni, setComuni] = useState<Comune[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Se non ho la sigla della provincia, non faccio la chiamata
        if (!siglaProvincia) {
            setLoading(false);
            return;
        }

        // Funzione auto-invocata async per fare la chiamata API
        (async () => {
            try {
                setLoading(true);
                setError(null);

                // Chiamata POST all'endpoint /comuni con siglaProvincia come body
                const response = await axios.post<Comune[]>(`${API_BASE_URL}/comuni`, {
                    siglaProvincia,
                });

                setComuni(response.data);
            } catch (err) {
                setError("Impossibile recuperare i dati dei comuni");
                console.error("Errore in useGetComuni:", err);
            } finally {
                setLoading(false);
            }
        })();
    }, [siglaProvincia]);

    return { comuni, loading, error };
};

export default useGetComuni;